import React, { Fragment, PureComponent } from 'react';
import { ExchangeRates, Rate, TimePeriod } from '~/Api/User/ExchangeRate';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { formatNumericHumanDate } from '~/DateTime';
import DatedRatesProvider from '~/ExchangeRate/DatedRatesProvider';
import RatesForm from '~/ExchangeRate/RatesForm/RatesForm';
import { RateTimePeriod } from '~/ExchangeRate/RateTimePeriod';
import { QuarterPicker } from '~/ExchangeRate/SelectModal/QuarterPicker';
import SelectModalControls from '~/ExchangeRate/SelectModalControls/SelectModalControlsContainer';
import ActivityIndicator from '~/UI/ActivityIndicator';
import { Link } from '~/UI/Button';
import redMicroButtonStyles from '~/UI/Button/red-micro.less';
import DatePicker from '~/UI/DatePicker/DatePicker';
import Heading from '~/UI/Heading/Heading';
import ModalBody from '~/UI/Modal/Body/ModalBody';
import modalCompactStyles from '~/UI/Modal/compact-wider.less';
import ModalContent from '~/UI/Modal/Content/ModalContent';
import ModalFooter from '~/UI/Modal/Footer/ModalFooter';
import ModalHeader from '~/UI/Modal/Header/ModalHeader';
import Modal from '~/UI/Modal/Modal';
import Radio from '~/UI/Radio';

import styles from './styles.less';

interface Props {
	rates: ExchangeRates[] | null;
	customRates: ExchangeRates[] | null;
	initiallySelectedRates: ExchangeRates | null;
	initiallySelectedDate: Date | null;
	group: UserProductGroup;
	isFetching: boolean;
	isUpdating: boolean;
	formatDate(date: Date): string;
	styles: {
		row: string;
		column: string;
		paragraph: string;
		warning: string;
		option: string;
		optionLabel: string;
		customRateSelected: string;
		customRate: string;
		customRateName: string;
		customRateNameSelected: string;
		discardDateParagraph: string;
	};
	onClose(): void;
	onLoad(groupId: string): void;
	onCreate(groupId: string, name: string, currency: string, rates: Rate[]): Promise<void>;
	onSelect(groupId: string, rates: ExchangeRates, date: Date | null): Promise<void>;
	onUpdate(groupId: string, ratesId: string, name: string, currency: string, rates: Rate[]): Promise<void>;
}

interface State {
	selectedRates: ExchangeRates | null;
	selectedDate: Date | null;
	selectedBaseCurrency: string | null;
	name: string | null;
	changedRates: {
		[currency: string]: {
			raw: string;
			parsed: number | null;
			isValid: boolean;
		};
	};
}

export default class SelectModal extends PureComponent<Props, State> {
	static defaultProps = {
		styles,
	};

	state: State = {
		selectedRates: null,
		selectedDate: null,
		selectedBaseCurrency: null,
		name: null,
		changedRates: {},
	};

	componentDidMount() {
		const { group, onLoad } = this.props;
		onLoad(group.id);
	}

	resolveSelectedRates(): ExchangeRates | null {
		const {
			props: { initiallySelectedRates },
			state: { selectedRates },
		} = this;
		return selectedRates || initiallySelectedRates;
	}

	resolveSelectedDate(): Date | null {
		const {
			props: { initiallySelectedDate },
			state: { selectedRates, selectedDate },
		} = this;
		return selectedDate || (selectedRates ? null : initiallySelectedDate);
	}

	resolveSelectedBaseCurrency(): string | null {
		const {
			props: { initiallySelectedRates },
			state: { selectedRates, selectedBaseCurrency },
		} = this;
		return selectedBaseCurrency ?? selectedRates?.baseCurrency ?? initiallySelectedRates?.baseCurrency ?? null;
	}

	resolveName(selectedRates: ExchangeRates | null): string {
		const { name } = this.state;
		if (name !== null) {
			return name;
		}
		if (!selectedRates) {
			return '';
		}
		if (selectedRates.isCustom()) {
			return selectedRates.name;
		}
		return `Custom ${selectedRates.name} ${formatNumericHumanDate(new Date())}`;
	}

	updateRates(rates: ExchangeRates | null) {
		this.setState({
			selectedRates: rates,
			selectedDate: null,
			name: null,
			changedRates: {},
		});
	}

	renderRates(selectedRates: ExchangeRates, selectedDate: Date | null) {
		const { rates, customRates, styles } = this.props;

		if (!rates) {
			return null;
		}

		return (
			<Fragment>
				{rates.map((rates) => (
					<div className={styles.option} key={rates.type}>
						<Radio checked={rates.type === selectedRates.type} name='rate' onChange={() => this.updateRates(rates)}>
							<strong>{rates.name}</strong>
							<p className={styles.optionLabel}>
								<RateTimePeriod
									date={rates.type === selectedRates.type ? selectedDate ?? rates.date : rates.date}
									timePeriod={rates.timePeriod}
								/>
							</p>
						</Radio>
					</div>
				))}
				<div className={styles.option}>
					<Radio
						disabled={customRates?.length === 0}
						checked={selectedRates.type === 'custom'}
						name='rate'
						onChange={() => this.updateRates((customRates || [])[0])}
					>
						<strong>Custom</strong>
						<p className={styles.optionLabel}>Setup own exchange rates</p>
					</Radio>
				</div>
			</Fragment>
		);
	}

	renderCustomRates(selectedRates: ExchangeRates) {
		const { customRates, formatDate, styles } = this.props;

		if (!customRates || !selectedRates) {
			return null;
		}

		return (
			<Fragment>
				<p className={styles.paragraph}>
					<strong>Select custom</strong> exchange rates <strong>list</strong> or <strong>create new</strong> one by
					editing exchange rate values, then assigning new new name to it.
				</p>

				{customRates.map((rates) => {
					const isActive = rates.id === selectedRates.id;

					return (
						<div
							className={isActive ? styles.customRateSelected : styles.customRate}
							key={rates.id}
							onClick={() => this.updateRates(rates)}
						>
							<p>
								<strong className={isActive ? styles.customRateNameSelected : styles.customRateName}>
									{rates.name}
								</strong>
							</p>
							{formatDate(rates.date)}
						</div>
					);
				})}
			</Fragment>
		);
	}

	renderDateSelection(selectedRates: ExchangeRates) {
		const { styles } = this.props;
		const disabledDays =
			selectedRates.availableDateRange !== null
				? {
						before: selectedRates.availableDateRange.since,
						after: selectedRates.availableDateRange.until,
				  }
				: { daysOfWeek: [0, 1, 2, 3, 4, 5, 6] };

		return (
			<Fragment>
				<p className={styles.paragraph}>
					You can <strong>choose historical exchange rates</strong> for selected exchange rates list.
				</p>
				<p className={styles.paragraph}>
					<strong>Current exchange rates</strong> will be used if <strong>you will not make the selection</strong>
				</p>

				{selectedRates.timePeriod === TimePeriod.previousQuarterAvg ? (
					<QuarterPicker
						allowedRange={
							selectedRates.availableDateRange !== null
								? [selectedRates.availableDateRange.since, selectedRates.availableDateRange.until]
								: undefined
						}
						disabled={selectedRates.availableDateRange === null}
						value={this.resolveSelectedDate()}
						onSelect={(date) => {
							this.setState({
								selectedRates,
								selectedDate: date,
								name: null,
								changedRates: {},
							});
						}}
					/>
				) : (
					<DatePicker
						disabledDays={disabledDays}
						value={this.resolveSelectedDate()}
						onSelect={(date) =>
							this.setState({
								selectedRates,
								selectedDate: date,
								name: null,
								changedRates: {},
							})
						}
					/>
				)}

				{selectedRates.availableDateRange !== null ? (
					<p className={styles.discardDateParagraph}>
						Keep current rates:{' '}
						<Link
							styles={redMicroButtonStyles}
							onClick={() =>
								this.setState({
									selectedRates,
									selectedDate: null,
									name: null,
									changedRates: {},
								})
							}
						>
							Discard selected date
						</Link>
					</p>
				) : null}
			</Fragment>
		);
	}

	formSubmitted = async (event: React.FormEvent<HTMLFormElement>, selectedRates: ExchangeRates | null) => {
		const {
			props: { group, onSelect, onCreate, onUpdate, onClose },
			state: { changedRates },
		} = this;
		const selectedDate = this.resolveSelectedDate();
		const selectedBaseCurrency = this.resolveSelectedBaseCurrency();
		event.preventDefault();

		if (!selectedRates || !selectedBaseCurrency) {
			return;
		}

		const name = this.resolveName(selectedRates);
		const rates = selectedRates.calculateExchangeRatesFor(selectedBaseCurrency).map((rate) => {
			return changedRates[rate.currency] !== undefined
				? rate.factorUpdated(String(changedRates[rate.currency].parsed))
				: rate;
		});

		try {
			if (selectedRates.isCustom()) {
				await onUpdate(group.id, selectedRates.id, name, selectedBaseCurrency, rates);
			} else if (Object.keys(changedRates).length > 0 || selectedRates.baseCurrency !== selectedBaseCurrency) {
				await onCreate(group.id, name, selectedBaseCurrency, rates);
			} else {
				await onSelect(group.id, selectedRates, selectedDate);
			}
			onClose();
		} catch (error) {
			// handled in action it self
		}
	};

	render() {
		const {
			props: { group, isUpdating, isFetching, styles, formatDate, onClose },
			state: { changedRates },
		} = this;
		const selectedRates = this.resolveSelectedRates();
		const selectedDate = this.resolveSelectedDate();
		const selectedBaseCurrency = this.resolveSelectedBaseCurrency();

		return (
			<DatedRatesProvider date={selectedDate} groupId={group.id} rates={selectedRates}>
				{(datedRates, unavailableDateError) => (
					<Modal styles={modalCompactStyles} onClose={isUpdating ? undefined : onClose}>
						<form style={{ height: '100%' }} onSubmit={(event) => this.formSubmitted(event, datedRates)}>
							<ModalContent>
								<ModalHeader>
									<Heading level={2}>Exchange Rates</Heading>
								</ModalHeader>
								<ModalBody>
									{selectedRates ? (
										<div className={styles.row}>
											<div className={styles.column}>
												<p className={styles.paragraph}>
													Choose <strong>predefined</strong> (updated daily) <strong>exchange rates list</strong> or{' '}
													<strong>define your own</strong>.
												</p>
												{this.renderRates(selectedRates, selectedDate)}
											</div>
											<div className={styles.column}>
												{selectedRates.isCustom()
													? this.renderCustomRates(selectedRates)
													: this.renderDateSelection(selectedRates)}
											</div>
											<div className={styles.column}>
												{unavailableDateError ? (
													<p className={styles.warning}>
														<strong>
															There are no data for selected exchange rates and date. Try nearest dates:{' '}
															{unavailableDateError.suggestedDates.map((date) => formatDate(date)).join(', ')}
														</strong>
													</p>
												) : null}

												{datedRates ? (
													<RatesForm
														baseCurrency={selectedBaseCurrency}
														changedRates={changedRates}
														selectedRates={datedRates}
														onBaseCurrencyChange={(selectedBaseCurrency) => this.setState({ selectedBaseCurrency })}
														onChange={(changedRates) => this.setState({ changedRates })}
													/>
												) : null}
											</div>
										</div>
									) : isFetching ? (
										<ActivityIndicator />
									) : null}
								</ModalBody>
								<ModalFooter>
									<SelectModalControls
										disableSubmit={isUpdating || isFetching || !datedRates}
										group={group}
										changedRates={changedRates}
										isUpdating={isUpdating}
										name={this.resolveName(selectedRates)}
										selectedBaseCurrency={selectedBaseCurrency}
										selectedDate={selectedDate}
										selectedRates={selectedRates}
										onClose={onClose}
										onNameChange={(name) => this.setState({ name })}
									/>
								</ModalFooter>
							</ModalContent>
						</form>
					</Modal>
				)}
			</DatedRatesProvider>
		);
	}
}
